import { DateWithSession, Session } from '@eventbrite/checkout-fairy-api';
import { track } from '@eventbrite/datalayer-library';
import { format } from 'date-fns';
import { History } from 'history';
import { FC } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { trackSeriesTicketsClick } from '../../actions/eventAnalytics';
import { loadSessionsForMonth } from '../../actions/eventSessions';
import { navigateToTicketSelection } from '../../actions/initialize';
import { notifyParentInitialPageFullyLoaded } from '../../actions/notifyParent';
import {
    initializeSeriesEvents,
    updateSelectedDateWithSession,
} from '../../actions/series';
import { HeapEventName } from '../../constants/heap/events';
import {
    getAppLocale,
    getCurrentTimezone,
    getIsAppRebrandActive,
} from '../../selectors/app';
import { getShouldShowCalendarSelection } from '../../selectors/app/getShouldShowCalendarSelection';
import {
    getEventEndDate,
    getEventFromStartDateToEndDate,
    getEventStartDate,
    getEventTimezone,
    getIsEventOnline,
} from '../../selectors/event';
import { getEventDates } from '../../selectors/event/getEventDates';
import { isSeriesInfiniteCalendar } from '../../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import {
    getSelectedDateWithSession,
    getSeriesIsLoading,
} from '../../selectors/series';
import { CheckoutState } from '../../state';
import { normalizeLocale } from '../../utils/general';
import { SeriesPageProps } from './SeriesPage';

export const connectSeriesPage = (component: FC<SeriesPageProps>) =>
    connect(
        (state: CheckoutState, ownProps: { router: History }) => ({
            endDate: getEventEndDate(state),
            get eventDates() {
                return this.shouldShowCalendarSelection
                    ? getEventDates(state)
                    : {};
            },
            eventFromStartDateToEndDate: getEventFromStartDateToEndDate(state),
            eventTimezone: getEventTimezone(state),
            isAppRebrandActive: getIsAppRebrandActive(state),
            shouldShowCalendarSelection: getShouldShowCalendarSelection(state),
            isEventOnline: getIsEventOnline(state),
            isLoading: getSeriesIsLoading(state),
            isSeriesInfiniteCalendar: isSeriesInfiniteCalendar(state),
            locale: normalizeLocale(getAppLocale(state)),
            startDate: getEventStartDate(state),
            selectedDateWithSession: getSelectedDateWithSession(state),
            timezone: getEventTimezone(state),
            currentTimezone: getCurrentTimezone(state),
            router: ownProps?.router,
        }),
        (dispatch: Dispatch<CheckoutState>) => ({
            onPageMountedLegacy: () => dispatch(initializeSeriesEvents()),
            onContinue: (eventId: string) => {
                dispatch(trackSeriesTicketsClick(eventId));
                dispatch(navigateToTicketSelection(eventId));
            },
            onMonthChanged: (date: Date, timezone: string) => {
                dispatch(loadSessionsForMonth(date, timezone));
                track({
                    eventName: HeapEventName.timedEntryMonthSelectorClick,
                    eventData: {
                        selectedOption: format(date, 'MMMM'),
                    },
                });
            },
            onDateChanged: (date: Date) => {
                track({
                    eventName: HeapEventName.timedEntryDateSelectorClick,
                    eventData: {
                        selectedOption: format(date, 'MM-dd-yyyy'),
                    },
                });
            },
            onPageFullyLoaded: () => {
                dispatch(notifyParentInitialPageFullyLoaded());
            },
            onSessionSelectedChanged: (dateWithSession: {
                date: DateWithSession;
                session: Session;
            }) => {
                dispatch(updateSelectedDateWithSession(dateWithSession));
                track({
                    eventName: HeapEventName.timedEntrySessionSelectorClick,
                    eventData: {
                        selectedOption: format(
                            new Date(
                                dateWithSession.session.startDatetime || '',
                            ),
                            'HH:mm',
                        ),
                    },
                });
            },
        }),
        (
            stateProps: Partial<SeriesPageProps>,
            dispatchProps: Partial<SeriesPageProps>,
            ownProps: any,
        ) => ({
            ...ownProps,
            ...stateProps,
            ...dispatchProps,
            onPageMounted: () =>
                stateProps.shouldShowCalendarSelection
                    ? null
                    : dispatchProps.onPageMountedLegacy?.(),
        }),
    )(component);
